import { CLIENT_COLORS, CYCLE, TASK_STATUS_COLORS, PRI_DOT } from "../constants";
import { isoToday, curMonth, fmtDate } from "../utils";
import { Dot, Tag, Card, Empty, ProgressBar, Stage, Today, KpiStrip, Explica, BlockHead, ExpandTable } from "../components/primitives";

export function DashboardView({ clients, tasks, events, kpis = [], planos = [], onGoClient }) {
  const today = isoToday();
  const month = curMonth();
  const week  = Math.ceil(new Date().getDate() / 7);
  const todayDay = new Date().getDate();

  const urgent   = tasks.filter((t) => t.priority === "Alta" && t.status !== "Concluído" && !t.archived);
  const pendente = tasks.filter((t) => t.status !== "Concluído" && !t.archived);
  const concluidos = tasks.filter((t) => t.status === "Concluído" && t.month === month);
  const todayEvts  = events.filter((e) => e.event_date === today).slice(0, 3);

  const weekLabel = ["Semana 1","Semana 2","Semana 3","Semana 4","Semana 5"][week - 1] || "Semana 4";

  const now = new Date();
  const dtStr = now.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

  const totalSpend   = kpis.reduce((s, k) => s + (k.spend || 0), 0);
  const kpiAlerts    = kpis.filter(k => (k.frequency >= 3.0) || ((k.ctr * 100) < 0.5));

  const semPlano = clients.filter((c) => {
    if (c.status !== "Ativo" || c.type === "Interno") return false;
    return !planos.some((p) => p.client_id === c.id && p.month === month);
  });

  const onboardingTravado = clients.filter((c) => {
    if (c.status !== "Ativo" || c.cycle_stage !== "1-onboarding") return false;
    if (!c.created_at) return false;
    const dias = Math.floor((new Date() - new Date(c.created_at)) / 86400000);
    return dias > 30;
  });

  const relatorioProximo = clients.filter((c) => {
    if (c.status !== "Ativo" || !c.dia_relatorio) return false;
    const diff = c.dia_relatorio - todayDay;
    return diff >= 0 && diff <= 2;
  });

  const acPendentes = clients.filter((c) => {
    const clientTasks = tasks.filter((t) => t.client_id === c.id && t.type === "AC" && t.month === month);
    return clientTasks.length === 0 || clientTasks.every((t) => t.status !== "Concluído");
  }).filter((c) => c.status === "Ativo" && c.type !== "Interno");

  const atPendentes = week >= 3 ? clients.filter((c) => {
    const clientTasks = tasks.filter((t) => t.client_id === c.id && t.type === "AT" && t.month === month);
    return clientTasks.length === 0 || clientTasks.every((t) => t.status !== "Concluído");
  }).filter((c) => c.status === "Ativo" && c.type !== "Interno") : [];

  const dtCap = dtStr.charAt(0).toUpperCase() + dtStr.slice(1);
  const brl = (v) => `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // Resumo antes do detalhe: no máximo 3 coisas para hoje, da mais grave para a mais leve.
  const hoje = [];
  if (urgent.length > 0) {
    const t0 = urgent[0];
    const c0 = clients.find((c) => c.id === t0.client_id);
    hoje.push({ tone: "bad", icon: "!", title: `${urgent.length} tarefa${urgent.length > 1 ? "s" : ""} urgente${urgent.length > 1 ? "s" : ""}`, sub: `Começar por: ${t0.name}${c0 ? ` · ${c0.name}` : ""}`, action: c0 ? "Abrir" : null, onAction: () => c0 && onGoClient(c0.id) });
  }
  if (atPendentes.length > 0) hoje.push({ tone: "bad", icon: "AT", title: `AT pendente: ${atPendentes.length} cliente${atPendentes.length > 1 ? "s" : ""}`, sub: atPendentes.slice(0, 3).map((c) => c.name).join(", "), action: "Abrir", onAction: () => onGoClient(atPendentes[0].id) });
  if (kpiAlerts.length > 0) {
    const c0 = clients.find((c) => c.id === kpiAlerts[0].client_id);
    hoje.push({ tone: "warn", icon: "↯", title: `Alerta de campanha em ${kpiAlerts.length}`, sub: "Frequência alta ou CTR baixo. Ver os detalhes abaixo.", action: c0 ? "Abrir" : null, onAction: () => c0 && onGoClient(c0.id) });
  }
  if (acPendentes.length > 0) hoje.push({ tone: "warn", icon: "AC", title: `AC pendente: ${acPendentes.length} cliente${acPendentes.length > 1 ? "s" : ""}`, sub: acPendentes.slice(0, 3).map((c) => c.name).join(", "), action: "Abrir", onAction: () => onGoClient(acPendentes[0].id) });
  if (relatorioProximo.length > 0) hoje.push({ tone: "ok", icon: "▤", title: `Relatório chegando: ${relatorioProximo.length}`, sub: relatorioProximo.map((c) => c.name).join(", "), action: "Abrir", onAction: () => onGoClient(relatorioProximo[0].id) });
  const totalAlertas = (urgent.length > 0) + (atPendentes.length > 0) + (kpiAlerts.length > 0) + (acPendentes.length > 0) + (relatorioProximo.length > 0) + (semPlano.length > 0) + (onboardingTravado.length > 0);

  const kpiItems = [
    { label: "Urgentes", value: urgent.length, sub: urgent.length ? "prioridade alta" : "nenhuma", tone: urgent.length ? "bad" : "ok" },
    { label: "Em aberto", value: pendente.length, sub: "tarefas" },
    { label: "Concluídas no mês", value: concluidos.length, sub: month, tone: "ok" },
    { label: "Clientes ativos", value: clients.filter((c) => c.status === "Ativo").length, sub: `${semPlano.length} sem plano` , tone: semPlano.length ? "warn" : "" },
  ];
  if (totalSpend > 0) kpiItems.push({ label: "Investido", value: brl(totalSpend), sub: "KPIs carregados" });

  const linhaCliente = (c, extra) => (
    <button key={c.id} type="button" className="btn btn-text" onClick={() => onGoClient(c.id)} style={{ justifyContent: "flex-start", padding: "3px 0", fontSize: 13 }}>
      → {c.name}{extra ? <span style={{ color: "var(--faint)", marginLeft: 6 }}>{extra}</span> : null}
    </button>
  );

  return (
    <div className="view">
      <Stage
        path="dashboard"
        title="Painel do"
        hl="dia"
        lead={`${dtCap} · ${weekLabel} de ${month}`}
        hasToday
      />
      <Today note={totalAlertas > 3 ? `3 de ${totalAlertas} alertas` : null} items={hoje} />

      <KpiStrip items={kpiItems} />

      {/* Alertas: título visível, lista ao abrir */}
      {(kpiAlerts.length > 0 || acPendentes.length > 0 || atPendentes.length > 0 || semPlano.length > 0 || onboardingTravado.length > 0 || relatorioProximo.length > 0) && (
        <>
          <BlockHead title="Alertas" sub="Abra para ver os clientes de cada um." />
          <div style={{ display: "grid", gap: 8 }}>
            {kpiAlerts.length > 0 && (
              <Explica tone="warn" title={`Alerta de campanha (${kpiAlerts.length})`}>
                <div style={{ display: "grid" }}>
                  {kpiAlerts.map((k) => {
                    const client = clients.find((c) => c.id === k.client_id);
                    const msgs = [];
                    if (k.frequency >= 3.5) msgs.push(`Frequência crítica: ${k.frequency.toFixed(2)}`);
                    else if (k.frequency >= 3.0) msgs.push(`Frequência alta: ${k.frequency.toFixed(2)}`);
                    if ((k.ctr * 100) < 0.5) msgs.push(`CTR baixo: ${(k.ctr * 100).toFixed(2)}%`);
                    return client
                      ? linhaCliente(client, msgs.join(" · "))
                      : <div key={k.id} style={{ fontSize: 13 }}>→ — {msgs.join(" · ")}</div>;
                  })}
                </div>
              </Explica>
            )}
            {atPendentes.length > 0 && (
              <Explica tone="bad" title={`AT pendente — ${weekLabel} (${atPendentes.length})`}>
                <div style={{ display: "grid" }}>{atPendentes.slice(0, 4).map((c) => linhaCliente(c))}</div>
              </Explica>
            )}
            {acPendentes.length > 0 && (
              <Explica tone="warn" title={`AC pendente este mês (${acPendentes.length})`}>
                <div style={{ display: "grid" }}>{acPendentes.slice(0, 4).map((c) => linhaCliente(c))}</div>
              </Explica>
            )}
            {semPlano.length > 0 && (
              <Explica tone="warn" title={`Sem plano este mês (${semPlano.length})`}>
                <div style={{ display: "grid" }}>{semPlano.slice(0, 4).map((c) => linhaCliente(c))}</div>
              </Explica>
            )}
            {onboardingTravado.length > 0 && (
              <Explica tone="warn" title={`Onboarding +30 dias (${onboardingTravado.length})`}>
                <div style={{ display: "grid" }}>
                  {onboardingTravado.map((c) => linhaCliente(c, `(${Math.floor((new Date() - new Date(c.created_at)) / 86400000)}d)`))}
                </div>
              </Explica>
            )}
            {relatorioProximo.length > 0 && (
              <Explica tone="ok" title={`Relatório chegando (${relatorioProximo.length})`}>
                <div style={{ display: "grid" }}>
                  {relatorioProximo.map((c) => { const diff = c.dia_relatorio - todayDay; return linhaCliente(c, diff === 0 ? "(hoje)" : `(${diff}d)`); })}
                </div>
              </Explica>
            )}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* Tarefas pendentes */}
        <div style={{ flex: 2, minWidth: 0, flexBasis: 420 }}>
          <BlockHead title="Tarefas pendentes" sub={pendente.length > 12 ? `12 de ${pendente.length}` : null} />
          {pendente.length === 0 ? (
            <Card><Empty icon="✅" text="Tudo em dia! Nenhuma tarefa pendente." /></Card>
          ) : (
            <ExpandTable
              minWidth={520}
              rows={pendente.slice(0, 12)}
              columns={[
                { key: "name", label: "Tarefa", render: (t) => (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Dot color={PRI_DOT[t.priority] || "var(--faint)"} size={7} />
                    <span className="name">{t.name}</span>
                  </span>
                ) },
                { key: "client", label: "Cliente", render: (t) => <span className="sub">{clients.find((c) => c.id === t.client_id)?.name || "—"}</span> },
                { key: "due", label: "Prazo", render: (t) => <span className="t-data" style={{ fontSize: 12.5 }}>{t.due_date ? fmtDate(t.due_date) : "—"}</span> },
                { key: "type", label: "Situação", render: (t) => {
                  const sc = TASK_STATUS_COLORS[t.status] || TASK_STATUS_COLORS["A fazer"];
                  return <Tag label={`${t.type} · ${t.status}`} bg={sc.bg} color={sc.tx} xs />;
                } },
              ]}
              detail={(t) => {
                const client = clients.find((c) => c.id === t.client_id);
                const done = (t.checklist || []).filter((x) => x.done).length;
                const total = (t.checklist || []).length;
                return (
                  <div style={{ display: "grid", gap: 10, paddingTop: 6 }}>
                    <dl className="facts">
                      <dt>Prioridade</dt><dd>{t.priority || "—"}</dd>
                      <dt>Mês</dt><dd>{t.month || "—"}</dd>
                      {total > 0 && <><dt>Itens</dt><dd>{done}/{total}</dd></>}
                    </dl>
                    {total > 0 && <ProgressBar done={done} total={total} />}
                    {client && <div><button type="button" className="btn btn-primary btn-sm" onClick={() => onGoClient(client.id)}>Abrir {client.name}</button></div>}
                  </div>
                );
              }}
            />
          )}
        </div>

        {/* Eventos de hoje e clientes */}
        <div style={{ flex: 1, minWidth: 0, flexBasis: 260 }}>
          <BlockHead title="Eventos de hoje" />
          {todayEvts.length === 0 ? (
            <div className="klist"><div className="klist-row"><span className="txt" style={{ color: "var(--faint)" }}>Nenhum evento hoje</span></div></div>
          ) : (
            <div className="klist">
              {todayEvts.map((e) => (
                <div key={e.id} className="klist-row">
                  <span className="txt" style={{ fontWeight: 600 }}>{e.title}</span>
                  {e.event_time && <span className="aside">{e.event_time.slice(0, 5)}</span>}
                </div>
              ))}
            </div>
          )}

          <BlockHead title="Clientes ativos" />
          <div className="table-card">
            {clients.filter((c) => c.status === "Ativo").map((c, i) => {
              const col    = CLIENT_COLORS[c.type] || CLIENT_COLORS["Interno"];
              const stage  = CYCLE.find((s) => s.id === c.cycle_stage) || CYCLE[5];
              const cTasks = tasks.filter((t) => t.client_id === c.id && !t.archived && t.month === month);
              const done   = cTasks.filter((t) => t.status === "Concluído").length;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onGoClient(c.id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px", background: "none", border: 0, borderTop: i ? "1px solid var(--line-soft)" : 0, cursor: "pointer", textAlign: "left", color: "inherit", font: "inherit" }}
                >
                  <Dot color={col.dot} size={8} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--muted)" }}>{stage.l}</div>
                  </div>
                  <span className="t-data" style={{ fontSize: 12, color: cTasks.length && done === cTasks.length ? "var(--success)" : "var(--faint)" }}>{done}/{cTasks.length}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
